import React, { useEffect, useState } from 'react';
import { sb } from '../../supabase';

// "Forgot password?" for EMAIL accounts, entirely by code — no link, no deep
// link, no second tab. Mirrored in the app (-maabar-app LoginScreen →
// ForgotPasswordPanel) with the same three steps and the same copy:
//
//   1. email → sb.auth.resetPasswordForEmail(email). The Send Email Hook
//      (supabase/functions/send-email, actionType === 'recovery') mails a code.
//      The confirmation shown is the SAME whether or not the address has an
//      account — GoTrue answers 200 for an unknown address, and so do we.
//   2. code  → sb.auth.verifyOtp({ email, token, type: 'recovery' }). Success
//      returns a real session (auth-js saves it and emits PASSWORD_RECOVERY).
//   3. new password → sb.auth.updateUser({ password }) on that session, then
//      the parent's onDone(user) routes exactly as a password sign-in does.
//
// Phone accounts are not served here: they sign in with a WhatsApp code
// (Login.jsx sendSigninCode), which needs no password at all.
//
// Code length: not pinned. The hosted project has issued 8 digits while
// supabase/config.toml says 6, so anything from 6 to 10 digits is accepted
// and Supabase rejects a wrong code regardless.
const CODE_MIN = 6;
const CODE_MAX = 10;
// Same minimum every sign-up form in the product enforces (BuyerRegister,
// ClaimFactory, the app's supplier sign-up) and auth.minimum_password_length.
const PASS_MIN = 6;
const COOLDOWN = 60;

const T = {
  ar: {
    title: 'إعادة تعيين كلمة المرور',
    emailIntro: 'أدخل بريدك الإلكتروني وسنرسل لك رمزاً لإعادة تعيين كلمة المرور.',
    email: 'البريد الإلكتروني',
    sendCode: 'أرسل الرمز',
    sent: 'إذا كان البريد مسجلاً لدينا، أرسلنا لك رمزاً.',
    codeLabel: 'رمز إعادة التعيين',
    codePh: 'الرمز',
    verify: 'تأكيد الرمز',
    resend: 'إعادة إرسال الرمز',
    resendIn: (n) => `إعادة الإرسال بعد ${n} ثانية`,
    changeEmail: 'تغيير البريد',
    newPass: 'كلمة المرور الجديدة',
    newPass2: 'أعد كتابة كلمة المرور الجديدة',
    passHint: `${PASS_MIN} أحرف على الأقل.`,
    save: 'حفظ كلمة المرور',
    done: 'تم تغيير كلمة المرور، وأنت الآن مسجّل الدخول.',
    continue: 'متابعة',
    backToSignin: 'رجوع لتسجيل الدخول',
    errEmail: 'تحقق من بريدك الإلكتروني — الصيغة غير صحيحة.',
    errCodeShort: 'أدخل الرمز كاملاً كما وصلك.',
    errCode: 'الرمز غير صحيح أو انتهت صلاحيته. اطلب رمزاً جديداً.',
    errShort: `كلمة المرور قصيرة — استخدم ${PASS_MIN} أحرف على الأقل.`,
    errMismatch: 'كلمتا المرور غير متطابقتين.',
    errSame: 'اختر كلمة مرور مختلفة عن السابقة.',
    errRate: 'طلبت رموزاً كثيرة متتالية. انتظر قليلاً ثم حاول مجدداً.',
    errGeneric: 'تعذّر إتمام العملية. حاول مرة أخرى.',
  },
  en: {
    title: 'Reset your password',
    emailIntro: "Enter your email and we'll send you a code to reset your password.",
    email: 'Email',
    sendCode: 'Send code',
    sent: "If this email is registered, we've sent you a code.",
    codeLabel: 'Reset code',
    codePh: 'Code',
    verify: 'Confirm code',
    resend: 'Resend code',
    resendIn: (n) => `Resend in ${n}s`,
    changeEmail: 'Change email',
    newPass: 'New password',
    newPass2: 'Repeat the new password',
    passHint: `At least ${PASS_MIN} characters.`,
    save: 'Save password',
    done: 'Your password has been changed and you are signed in.',
    continue: 'Continue',
    backToSignin: 'Back to sign in',
    errEmail: 'Check your email address — that format is not valid.',
    errCodeShort: 'Enter the full code exactly as you received it.',
    errCode: 'That code is wrong or has expired. Request a new one.',
    errShort: `That password is too short — use at least ${PASS_MIN} characters.`,
    errMismatch: 'The two passwords do not match.',
    errSame: 'Choose a password different from your old one.',
    errRate: 'Too many codes requested in a row. Wait a moment, then try again.',
    errGeneric: 'That did not go through. Please try again.',
  },
  zh: {
    title: '重置密码',
    emailIntro: '请输入您的邮箱，我们将发送重置密码的验证码。',
    email: '电子邮件',
    sendCode: '发送验证码',
    sent: '如果该邮箱已注册，我们已向您发送验证码。',
    codeLabel: '重置验证码',
    codePh: '验证码',
    verify: '确认验证码',
    resend: '重新发送验证码',
    resendIn: (n) => `${n} 秒后可重发`,
    changeEmail: '更换邮箱',
    newPass: '新密码',
    newPass2: '再次输入新密码',
    passHint: `至少 ${PASS_MIN} 个字符。`,
    save: '保存密码',
    done: '密码已修改，您已登录。',
    continue: '继续',
    backToSignin: '返回登录',
    errEmail: '请检查邮箱地址，格式不正确。',
    errCodeShort: '请按收到的内容完整输入验证码。',
    errCode: '验证码错误或已过期，请重新获取。',
    errShort: `密码太短 — 请至少使用 ${PASS_MIN} 个字符。`,
    errMismatch: '两次输入的密码不一致。',
    errSame: '请设置与旧密码不同的新密码。',
    errRate: '请求验证码过于频繁，请稍候再试。',
    errGeneric: '操作未完成，请重试。',
  },
};

function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isRateLimit(error) {
  const m = String(error?.message || '').toLowerCase();
  return error?.status === 429
    || error?.code === 'over_email_send_rate_limit'
    || m.includes('rate limit')
    || m.includes('for security purposes');
}

export default function ForgotPassword({
  lang = 'ar',
  initialEmail = '',
  inputStyle = {},
  labelStyle = {},
  helperTextStyle = {},
  primaryBtnStyle = {},
  onCancel,
  onDone,
}) {
  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';

  const [step, setStep] = useState('email');   // email | code | password | done
  const [email, setEmail] = useState(String(initialEmail || '').trim());
  const [code, setCode] = useState('');
  const [pass1, setPass1] = useState('');
  const [pass2, setPass2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [doneUser, setDoneUser] = useState(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const normalizedEmail = email.trim().toLowerCase();

  const sendCode = async ({ isResend = false } = {}) => {
    if (isResend && cooldown > 0) return;
    setErr(''); setInfo('');
    if (!looksLikeEmail(normalizedEmail)) { setErr(t.errEmail); return; }
    setBusy(true);
    // No redirectTo: the hook's recovery branch mails a code, never a link.
    const { error } = await sb.auth.resetPasswordForEmail(normalizedEmail);
    setBusy(false);
    if (error) {
      // GoTrue answers 200 for an address with no account, so an error here is
      // transport or rate limiting — never "no such user". Say what it is.
      console.error('[ForgotPassword] resetPasswordForEmail failed:', error.status || '', error.code || '', error.message || error);
      setErr(isRateLimit(error) ? t.errRate : t.errGeneric);
      return;
    }
    setStep('code');
    if (!isResend) setCode('');
    setCooldown(COOLDOWN);
    setInfo(t.sent);
  };

  const verifyCode = async () => {
    const token = code.replace(/\s/g, '');
    setErr(''); setInfo('');
    if (token.length < CODE_MIN) { setErr(t.errCodeShort); return; }
    setBusy(true);
    // Same guard Login.jsx sets around a password sign-in: this flow handles the
    // session itself, so App's onAuthStateChange must not reset the tree (and
    // unmount this panel) when the recovery session lands.
    window._passwordLoginTs = Date.now();
    const { data, error } = await sb.auth.verifyOtp({ email: normalizedEmail, token, type: 'recovery' });
    setBusy(false);
    if (error || !data?.session) {
      console.error('[ForgotPassword] verifyOtp(recovery) failed:', error?.status || '', error?.code || '', error?.message || 'no session');
      setErr(t.errCode);
      setCode('');
      return;
    }
    setStep('password');
  };

  const savePassword = async () => {
    setErr(''); setInfo('');
    if (pass1.length < PASS_MIN) { setErr(t.errShort); return; }
    if (pass1 !== pass2) { setErr(t.errMismatch); return; }
    setBusy(true);
    const { data, error } = await sb.auth.updateUser({ password: pass1 });
    setBusy(false);
    if (error) {
      console.error('[ForgotPassword] updateUser(password) failed:', error.status || '', error.code || '', error.message || error);
      const m = String(error.message || '').toLowerCase();
      if (error.code === 'same_password' || m.includes('different from the old')) setErr(t.errSame);
      else if (error.code === 'weak_password' || m.includes('at least')) setErr(t.errShort);
      else setErr(t.errGeneric);
      return;
    }
    setDoneUser(data?.user || null);
    setStep('done');
  };

  const linkBtn = (disabled) => ({
    background: 'none', border: 'none', padding: 0,
    cursor: disabled ? 'default' : 'pointer', fontSize: 12.5,
    color: disabled ? 'var(--text-disabled)' : 'var(--text-secondary)',
    textDecoration: disabled ? 'none' : 'underline', fontFamily: font,
  });
  const btn = (disabled) => ({
    ...primaryBtnStyle, width: '100%', marginTop: 14,
    opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
  });
  const field = { marginBottom: 16 };

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} data-clarity-mask="True" style={{ marginBottom: 20, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 16 }}>
      <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', fontFamily: font }}>
        {t.title}
      </p>

      {step === 'email' && (
        <>
          <p style={{ ...helperTextStyle, margin: '0 0 14px' }}>{t.emailIntro}</p>
          <div style={field}>
            <label style={labelStyle}>{t.email}</label>
            <input
              style={inputStyle}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErr(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy) sendCode(); }}
              placeholder="example@email.com"
              autoComplete="email"
              dir="ltr"
            />
          </div>
          {err && <p style={{ margin: '0 0 6px', fontSize: 12.5, color: '#d66b6b', fontFamily: font }}>{err}</p>}
          <button type="button" onClick={() => sendCode()} disabled={busy} style={btn(busy)}>
            {busy ? '...' : t.sendCode}
          </button>
        </>
      )}

      {step === 'code' && (
        <>
          {info && <p style={{ margin: '0 0 12px', fontSize: 13, color: '#3a7a52', lineHeight: 1.7, fontFamily: font }}>{info}</p>}
          <p style={{ ...helperTextStyle, margin: '0 0 12px' }}>
            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{normalizedEmail}</span>
          </p>
          <label style={labelStyle}>{t.codeLabel}</label>
          <input
            style={{ ...inputStyle, letterSpacing: 6, textAlign: 'center', fontFamily: 'Inter, sans-serif' }}
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_MAX)); setErr(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !busy) verifyCode(); }}
            placeholder={t.codePh}
            inputMode="numeric"
            autoComplete="one-time-code"
            dir="ltr"
          />
          {err && <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#d66b6b', fontFamily: font }}>{err}</p>}
          <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => sendCode({ isResend: true })}
              disabled={cooldown > 0 || busy}
              style={linkBtn(cooldown > 0 || busy)}
            >
              {cooldown > 0 ? t.resendIn(cooldown) : t.resend}
            </button>
            <button
              type="button"
              onClick={() => { setStep('email'); setCode(''); setErr(''); setInfo(''); }}
              style={linkBtn(false)}
            >
              {t.changeEmail}
            </button>
          </div>
          <button
            type="button"
            onClick={verifyCode}
            disabled={busy || code.length < CODE_MIN}
            style={btn(busy || code.length < CODE_MIN)}
          >
            {busy ? '...' : t.verify}
          </button>
        </>
      )}

      {step === 'password' && (
        <>
          <div style={{ ...field, marginTop: 10 }}>
            <label style={labelStyle}>{t.newPass}</label>
            <input
              style={inputStyle}
              type="password"
              value={pass1}
              onChange={(e) => { setPass1(e.target.value); setErr(''); }}
              placeholder="••••••••"
              autoComplete="new-password"
              dir="ltr"
            />
            <p style={helperTextStyle}>{t.passHint}</p>
          </div>
          <div style={field}>
            <label style={labelStyle}>{t.newPass2}</label>
            <input
              style={inputStyle}
              type="password"
              value={pass2}
              onChange={(e) => { setPass2(e.target.value); setErr(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy) savePassword(); }}
              placeholder="••••••••"
              autoComplete="new-password"
              dir="ltr"
            />
          </div>
          {err && <p style={{ margin: '0 0 6px', fontSize: 12.5, color: '#d66b6b', fontFamily: font }}>{err}</p>}
          <button type="button" onClick={savePassword} disabled={busy} style={btn(busy)}>
            {busy ? '...' : t.save}
          </button>
        </>
      )}

      {step === 'done' && (
        <>
          <p style={{ margin: '8px 0 0', fontSize: 13.5, color: '#3a7a52', lineHeight: 1.7, fontFamily: font }}>{t.done}</p>
          <button type="button" onClick={() => onDone?.(doneUser)} style={btn(false)}>
            {t.continue}
          </button>
        </>
      )}

      {/* Out of the flow before a session exists. Once the code is accepted the
          user IS signed in, so there is nothing to go "back" to — the only way
          forward is the new password. */}
      {(step === 'email' || step === 'code') && (
        <div style={{ marginTop: 14, textAlign: 'center' }}>
          <button type="button" onClick={() => onCancel?.()} style={linkBtn(false)}>
            {t.backToSignin}
          </button>
        </div>
      )}
    </div>
  );
}
